// Step 4 state: the membership check, the review screen, the paywall, checkout,
// and the two ways the flow ends (sent, or held for later).
//
// The membership check is the hinge. Both A/B arms route on it — variant A asks
// on the review screen, variant B asks on the paywall — so it lives here once
// rather than being re-derived per page, and `canSend` below is the ONLY
// definition of "may this submission be sent".

import { createModel } from "@rematch/core";
import {
  getSubmissionFlowApi,
  ICheckoutStatusDto,
  IHoldSubmissionDto,
  IHoldSubmissionRequest,
  IPaywallOfferDto,
  IRepresentationDto,
  ISendSubmissionResponse,
  ISetRepresentationRequest,
  ISubmissionMembershipDto,
  ISubmissionReviewDto,
  MockCheckoutOutcome,
  Step4Variant,
} from "../shared/api/submission";
import {
  adoptStep4Variant,
  consumeStep4Reset,
  isStep4Forced,
  STEP4_EXPERIMENT,
} from "../components/Submission/common/step4Variant";
import { FLOW_PARAMS, membershipPaywallUrl } from "../components/Submission/common/membershipPaywall";
import { isMockApi } from "../shared/api/submission";
import { getSubmissionAuth } from "../helpers/submissionAuth";
import { prefetched, reviewKey } from "../components/Submission/common/prefetch";
import { forgetBreakdownInfoOn } from "../components/Submission/common/breakdownInfo";
import { setSubmissionAnalyticsContext } from "../components/Submission/analytics";

// The events' market and plan (analytics.ts), from the server's membership answer.
const noteMembership = (membership: ISubmissionMembershipDto | null | undefined) => {
  if (!membership) return;
  setSubmissionAnalyticsContext({
    market: membership.countryCode || undefined,
    onPlan: membership.membershipStatus === "pro" || membership.membershipStatus === "trial",
  });
};

// Callers asking at the same moment share one request: step 2 opens with the header and
// the page both asking. Only while it's in flight, so the next ask always reaches the
// server and a plan bought meanwhile is seen. Shared only within one sign-in: after Log out,
// the next person never gets the previous one's answer.
let membershipInFlight: { jwt: string; asked: Promise<ISubmissionMembershipDto | null> } | null = null;
// Bumped as someone else takes the tab over (Log out, Continue as, the login's hand-off), from
// the moment it starts: until the new sign-in is stored the tab still holds the previous token,
// so the token alone can't tell their late answer from the new person's.
let membershipGeneration = 0;

export const forgetMembershipAsked = () => {
  membershipGeneration += 1;
  membershipInFlight = null;
};

const tabToken = () => (getSubmissionAuth() || { jwt: "" }).jwt;

// For an answer asked now that comes later: true while the same person still has the tab.
const askedByWhoeverIsHere = () => {
  const jwt = tabToken();
  const generation = membershipGeneration;
  return () => generation === membershipGeneration && tabToken() === jwt;
};

// After a purchase the plan shows up within seconds (Stripe's webhook, then the
// membership tables): poll this long before handing over to the background job.
const PLAN_POLL_DELAYS_MS = [500, 1000, 1500, 2000, 2500, 3000, 4000, 5000, 5000, 5000];

// Why the plan screen isn't the next step: paying would buy nothing for this
// submission. Any other failure recording the checkout (a lost write, an arm the
// server never got) must not stand between the actor and the plan screen; the
// page sends after the payment either way.
export const CHECKOUT_BLOCKERS = [
  "alreadySent",
  "alreadySubmitted",
  "breakdownClosed",
  "breakdownNotFound",
  // a new signup we can't place hasn't picked a film center: step 2 first, nothing paid
  "homeLocationRequired",
  "roleCast",
  "roleRequired",
  "submissionNotFound",
];
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// The idempotency key for the send, kept per tab: a retry after a lost response
// must reuse it, or the retry creates a second submission.
const SEND_KEY = "sfSendRequestId.v1";
// Survives the full-page trip through Stripe, which wipes React state.
const CHECKOUT_KEY = "sfCheckoutSession.v1";

export type CheckoutPhase =
  | "idle"
  | "creating"
  | "redirecting"
  | "confirming"
  | "complete"
  | "failed";

export interface ISubmissionStep4State {
  membership: ISubmissionMembershipDto | null;
  review: ISubmissionReviewDto | null;
  offer: IPaywallOfferDto | null;
  selectedServiceId: number | null;

  checkoutPhase: CheckoutPhase;
  checkoutSessionId: string | null;
  attemptId: number | null;
  // Set when a checkout was declined, abandoned or expired, so the paywall can
  // explain itself on the way back instead of just re-rendering.
  checkoutError: { code: string; message: string } | null;

  sent: ISendSubmissionResponse | null;
  hold: IHoldSubmissionDto | null;

  loading: boolean;
  error: { code: string; message: string } | null;
}

const initialState: ISubmissionStep4State = {
  membership: null,
  review: null,
  offer: null,
  selectedServiceId: null,

  checkoutPhase: "idle",
  checkoutSessionId: null,
  attemptId: null,
  checkoutError: null,

  sent: null,
  hold: null,

  loading: false,
  error: null,
};

const errorOf = (err: any): { code: string; message: string } => {
  if (err && err.isNetworkError) return { code: "network", message: err.message };
  if (err && err.errorCode) {
    // A breakdown that closed (or a role cast) meanwhile: the kept info is out of date.
    forgetBreakdownInfoOn(err.errorCode);
    return { code: err.errorCode, message: err.message || "" };
  }
  return { code: "unknown", message: (err && err.message) || "" };
};

// May this submission be sent? The one definition, derived from the server's
// answer and nothing else. A client that decides its own entitlement is a
// client that can be edited, so an absent check reads as "no".
export const canSend = (membership: ISubmissionMembershipDto | null): boolean =>
  !!membership &&
  (membership.canSubmitFree ||
    membership.membershipStatus === "pro" ||
    membership.membershipStatus === "trial");

// Already on Pro or a trial (decided 2026-10-27): no phone step, no plan offered,
// and the header says the submission is included.
export const onPlan = (membership: ISubmissionMembershipDto | null): boolean =>
  !!membership && (membership.membershipStatus === "pro" || membership.membershipStatus === "trial");

// Does this visitor need to see a paywall at all? US talent, existing Pro
// members and anyone mid-trial do not.
export const needsPaywall = (membership: ISubmissionMembershipDto | null): boolean =>
  !!membership && !canSend(membership);

const readStored = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // ignore
  }
};

// Stable per draft, so every retry of the same send carries the same key.
const sendRequestId = (): string => {
  const existing = readStored(SEND_KEY);
  if (existing) return existing;
  const id = `req_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  writeStored(SEND_KEY, id);
  return id;
};

// The plan the visitor has most recently chosen. A module-level ref rather than
// store state because it has to be readable AFTER an await inside an effect:
// rematch hands effects a rootState snapshot from call time, so reading the
// store again would compare the old value against itself and the guard below
// would never fire.
let latestServiceId: number | null = null;

// The job-alerts box as last set, for the same reason: a failed request only puts the box
// back when no later click has changed it since.
let latestJobAlerts: boolean | null = null;

const submissionStep4Model = createModel({
  state: initialState,

  reducers: {
    setLoading: (state: ISubmissionStep4State, loading: boolean) => ({
      ...state,
      loading,
    }),
    setError: (
      state: ISubmissionStep4State,
      error: { code: string; message: string } | null
    ) => ({ ...state, error, loading: false }),
    setMembership: (
      state: ISubmissionStep4State,
      membership: ISubmissionMembershipDto
    ) => ({ ...state, membership }),
    setReview: (state: ISubmissionStep4State, review: ISubmissionReviewDto) => ({
      ...state,
      review,
      // The review response carries the same membership block, so one call
      // answers both questions and they cannot disagree.
      membership: review.membership,
      loading: false,
    }),
    setRepresentation: (
      state: ISubmissionStep4State,
      representation: IRepresentationDto
    ) => ({
      ...state,
      review: state.review ? { ...state.review, representation } : state.review,
    }),
    setJobAlertsChecked: (state: ISubmissionStep4State, subscribed: boolean) => ({
      ...state,
      review: state.review
        ? { ...state.review, jobAlerts: { offered: true, subscribed, declined: !subscribed } }
        : state.review,
    }),
    setOffer: (state: ISubmissionStep4State, offer: IPaywallOfferDto) => ({
      ...state,
      offer,
      // Only adopt the offer's default the first time; a visitor who has already
      // picked Monthly must not be silently moved back to Annual on a refetch.
      selectedServiceId: state.selectedServiceId || offer.defaultServiceId,
      loading: false,
    }),
    selectService: (state: ISubmissionStep4State, serviceId: number) => ({
      ...state,
      selectedServiceId: serviceId,
    }),
    setCheckoutPhase: (state: ISubmissionStep4State, checkoutPhase: CheckoutPhase) => ({
      ...state,
      checkoutPhase,
    }),
    setCheckoutSession: (
      state: ISubmissionStep4State,
      payload: { checkoutSessionId: string; attemptId: number }
    ) => ({
      ...state,
      checkoutSessionId: payload.checkoutSessionId,
      attemptId: payload.attemptId,
      checkoutError: null,
    }),
    setCheckoutError: (
      state: ISubmissionStep4State,
      checkoutError: { code: string; message: string } | null
    ) => ({ ...state, checkoutError, checkoutPhase: checkoutError ? "failed" : "idle" }),
    setSent: (state: ISubmissionStep4State, sent: ISendSubmissionResponse) => ({
      ...state,
      sent,
      loading: false,
    }),
    setHold: (state: ISubmissionStep4State, hold: IHoldSubmissionDto) => ({
      ...state,
      hold,
      loading: false,
    }),
    reset: () => ({ ...initialState }),
  },

  effects: (dispatch: any) => ({
    // The hinge. Called on the entry screen of each arm, and again whenever a
    // checkout returns — payment is what changes the answer.
    async loadMembership() {
      const jwt = tabToken();
      if (membershipInFlight && membershipInFlight.jwt === jwt) return membershipInFlight.asked;
      // Answered after Log out or another sign-in in this tab, or once one has started: that
      // answer is the previous person's, and never reaches the state or the analytics of whoever
      // is here now.
      const stillTheirs = askedByWhoeverIsHere();
      const asked = (async () => {
        try {
          const membership = await getSubmissionFlowApi().getSubmissionMembership();
          if (!stillTheirs()) return null;
          dispatch.submissionStep4Model.setMembership(membership);
          noteMembership(membership);
          return membership;
        } catch (err) {
          if (stillTheirs()) dispatch.submissionStep4Model.setError(errorOf(err));
          return null;
        }
      })();
      const slot = { jwt, asked };
      membershipInFlight = slot;
      // Freed however it ends: a request that failed is never handed out again.
      const free = () => {
        if (membershipInFlight === slot) membershipInFlight = null;
      };
      asked.then(free, free);
      return asked;
    },

    async loadReview(submissionId: number) {
      dispatch.submissionStep4Model.setLoading(true);
      try {
        // Possibly already asked during the phone step's countdown (common/prefetch).
        const review = await prefetched(reviewKey(submissionId), () =>
          getSubmissionFlowApi().getSubmissionReview(submissionId)
        );
        dispatch.submissionStep4Model.setReview(review);
        noteMembership(review && review.membership);
        return review;
      } catch (err) {
        dispatch.submissionStep4Model.setError(errorOf(err));
        return null;
      }
    },

    // Step 4's "Subscribe to receive more breakdowns by email" (decided 2026-10-28). The box changes at
    // once and the request goes behind it: nothing on the page, the paywall or the send waits
    // for it, and the server makes the change in the background too. One retry; a second
    // failure puts the box back, unless it was clicked again meanwhile.
    async setJobAlerts(subscribe: boolean) {
      latestJobAlerts = subscribe;
      dispatch.submissionStep4Model.setJobAlertsChecked(subscribe);
      const ask = () => getSubmissionFlowApi().setJobAlerts(subscribe);
      ask()
        .catch(() => wait(3000).then(ask))
        .catch(() => {
          if (latestJobAlerts === subscribe) dispatch.submissionStep4Model.setJobAlertsChecked(!subscribe);
        });
    },

    async saveRepresentation(payload: {
      submissionId: number;
      request: ISetRepresentationRequest;
    }) {
      // The sheet shows its own error, so the page's stays clear.
      try {
        const representation = await getSubmissionFlowApi().setRepresentation(
          payload.submissionId,
          payload.request
        );
        dispatch.submissionStep4Model.setRepresentation(representation);
        return { representation, error: null };
      } catch (err) {
        return { representation: null, error: errorOf(err).code };
      }
    },

    // Toggling the plan goes through here rather than dispatching the reducer
    // directly, so the in-flight checkout guard below can see the change.
    async chooseService(serviceId: number) {
      latestServiceId = serviceId;
      dispatch.submissionStep4Model.selectService(serviceId);
      return serviceId;
    },

    async loadOffer(variant: Step4Variant) {
      dispatch.submissionStep4Model.setLoading(true);
      try {
        const offer = await getSubmissionFlowApi().getPaywallOffer(variant);
        dispatch.submissionStep4Model.setOffer(offer);
        return offer;
      } catch (err) {
        dispatch.submissionStep4Model.setError(errorOf(err));
        return null;
      }
    },

    // The arm this account is in. The page proposes its own (the browser's
    // 50/50, or a ?step4= pick); the server keeps the first one stored on the
    // account and answers it (docs/30 #19), and the page follows that answer.
    // An exposure that can't be written never blocks step 4: the page's own arm
    // stands.
    async recordExposure(payload: {
      variant: Step4Variant;
      sessionId?: string | null;
      breakdownId?: string | null;
      roleId?: string | null;
    }): Promise<Step4Variant> {
      try {
        const resp = await getSubmissionFlowApi().recordPaywallExposure({
          experiment: STEP4_EXPERIMENT,
          variant: payload.variant,
          forced: isStep4Forced(),
          reset: consumeStep4Reset(),
          sessionId: payload.sessionId,
          breakdownId: payload.breakdownId,
          roleId: payload.roleId,
        });
        const arm = resp && resp.arm ? resp.arm : payload.variant;
        adoptStep4Variant(arm);
        return arm;
      } catch (err) {
        return payload.variant;
      }
    },

    // Right before leaving for the plan screen: the deadline is checked once
    // more and the checkout recorded on the account (docs/30 §6, #22). Answers
    // where to go (the membership paywall, or in the mock its stand-in), or
    // the blocker's code when paying would buy nothing (CHECKOUT_BLOCKERS).
    async startMembershipCheckout(payload: {
      submissionId: number;
      search: string;
    }): Promise<{ url: string | null; blocked: string | null }> {
      dispatch.submissionStep4Model.setError(null);
      const url = isMockApi()
        ? `/submission/paywall${payload.search}`
        : membershipPaywallUrl(payload.search, payload.submissionId);
      try {
        await getSubmissionFlowApi().recordCheckoutStarted(payload.submissionId);
        return { url, blocked: null };
      } catch (err) {
        const error = errorOf(err);
        if (CHECKOUT_BLOCKERS.indexOf(error.code) < 0) return { url, blocked: null };
        dispatch.submissionStep4Model.setError(error);
        return { url: null, blocked: error.code };
      }
    },

    // Tests C and D: the plan without the membership paywall page. One call
    // records the checkout (as startMembershipCheckout) and starts the trial's
    // Stripe session; the answer is Stripe's page. Anything not right there (no
    // trial on offer, Pro already, an error) answers the paywall instead, as in
    // tests A and B. In the mock: its stand-in checkout, on the offer's default
    // plan, as the paywall would preselect it.
    async startDirectCheckout(payload: {
      submissionId: number;
      search: string;
      variant: Step4Variant;
    }): Promise<{ url: string | null; blocked: string | null; direct: boolean }> {
      dispatch.submissionStep4Model.setError(null);
      const paywall = isMockApi()
        ? `/submission/paywall${payload.search}`
        : membershipPaywallUrl(payload.search, payload.submissionId);
      const params: Record<string, string> = {};
      const incoming = new URLSearchParams(payload.search || "");
      FLOW_PARAMS.forEach((name) => {
        const value = incoming.get(name);
        if (value) params[name] = value;
      });
      try {
        if (isMockApi()) {
          await getSubmissionFlowApi().startDirectCheckout(payload.submissionId, params);
          const offer = await getSubmissionFlowApi().getPaywallOffer(payload.variant);
          await dispatch.submissionStep4Model.chooseService(offer.defaultServiceId);
          const resp: any = await dispatch.submissionStep4Model.startCheckout({
            variant: payload.variant,
            submissionId: payload.submissionId,
            returnPath: `/submission/flow/step/4${payload.search}`,
          });
          return resp && resp.redirectUrl
            ? { url: resp.redirectUrl, blocked: null, direct: true }
            : { url: paywall, blocked: null, direct: false };
        }
        const resp = await getSubmissionFlowApi().startDirectCheckout(payload.submissionId, params);
        return resp && resp.redirectUrl
          ? { url: resp.redirectUrl, blocked: null, direct: true }
          : { url: paywall, blocked: null, direct: false };
      } catch (err) {
        const error = errorOf(err);
        if (CHECKOUT_BLOCKERS.indexOf(error.code) < 0) return { url: paywall, blocked: null, direct: false };
        dispatch.submissionStep4Model.setError(error);
        return { url: null, blocked: error.code, direct: false };
      }
    },

    // Back from a purchase: wait until the plan is on the account. Resolves to
    // the membership answer once the account may send, or null when the plan
    // hasn't shown up in time (the background job sends it later).
    async waitForPlan() {
      // A poll answered after someone else took the tab over is theirs: the wait stops there.
      const stillTheirs = askedByWhoeverIsHere();
      for (const delay of PLAN_POLL_DELAYS_MS) {
        try {
          const membership = await getSubmissionFlowApi().getSubmissionMembership();
          if (!stillTheirs()) return null;
          dispatch.submissionStep4Model.setMembership(membership);
          // The events after the payment say "pro" whichever way the plan was confirmed.
          noteMembership(membership);
          if (canSend(membership)) return membership;
        } catch (err) {
          // a failed poll is just one fewer chance; keep going
        }
        await wait(delay);
      }
      return null;
    },

    async startCheckout(
      payload: { variant: Step4Variant; submissionId: number; returnPath: string },
      rootState: any
    ) {
      const state: ISubmissionStep4State = rootState.submissionStep4Model;
      // Double-click guard: a second session would be created, and only one of
      // them could ever complete.
      if (state.checkoutPhase === "creating" || state.checkoutPhase === "redirecting") {
        return null;
      }
      const serviceId = state.selectedServiceId;
      if (!serviceId) return null;
      latestServiceId = serviceId;

      dispatch.submissionStep4Model.setCheckoutPhase("creating");
      try {
        const resp = await getSubmissionFlowApi().startCheckout({
          serviceId,
          variant: payload.variant,
          submissionId: payload.submissionId,
          returnPath: payload.returnPath,
        });
        // The plan may have been toggled while this was in flight. The response
        // is for the OLD plan, so it is discarded rather than sending someone to
        // a checkout for a plan they just changed away from.
        if (latestServiceId !== serviceId) {
          dispatch.submissionStep4Model.setCheckoutPhase("idle");
          return null;
        }

        dispatch.submissionStep4Model.setCheckoutSession({
          checkoutSessionId: resp.checkoutSessionId,
          attemptId: resp.attemptId,
        });
        writeStored(
          CHECKOUT_KEY,
          JSON.stringify({
            checkoutSessionId: resp.checkoutSessionId,
            attemptId: resp.attemptId,
            returnPath: payload.returnPath,
            variant: payload.variant,
          })
        );
        dispatch.submissionStep4Model.setCheckoutPhase("redirecting");
        return resp;
      } catch (err) {
        dispatch.submissionStep4Model.setCheckoutError(errorOf(err));
        return null;
      }
    },

    // What the return page polls. Resolves to the status so the caller decides
    // where to go — this effect deliberately does not navigate.
    async pollCheckoutStatus(checkoutSessionId: string) {
      try {
        const status = await getSubmissionFlowApi().getCheckoutStatus(checkoutSessionId);
        if (status.status === "complete") {
          dispatch.submissionStep4Model.setCheckoutPhase("complete");
        }
        return status;
      } catch (err) {
        dispatch.submissionStep4Model.setCheckoutError(errorOf(err));
        return null;
      }
    },

    // Mock-only: the fake Stripe page reporting what the demo chose.
    async completeMockCheckout(payload: {
      checkoutSessionId: string;
      outcome: MockCheckoutOutcome;
    }): Promise<ICheckoutStatusDto | null> {
      try {
        const status = await getSubmissionFlowApi().completeMockCheckout(
          payload.checkoutSessionId,
          payload.outcome
        );
        return status;
      } catch (err) {
        dispatch.submissionStep4Model.setCheckoutError(errorOf(err));
        return null;
      }
    },

    async sendSubmission(submissionId: number) {
      dispatch.submissionStep4Model.setLoading(true);
      dispatch.submissionStep4Model.setError(null);
      try {
        const resp = await getSubmissionFlowApi().sendSubmission(submissionId, {
          clientRequestId: sendRequestId(),
        });
        dispatch.submissionStep4Model.setSent(resp);
        return resp;
      } catch (err) {
        const code = errorOf(err).code;
        // Already sent is a success from the visitor's point of view: the thing
        // they asked for has happened. Anything else is a real failure the
        // caller has to route on.
        if (code === "alreadySent") {
          dispatch.submissionStep4Model.setLoading(false);
          return { alreadySent: true } as any;
        }
        dispatch.submissionStep4Model.setError(errorOf(err));
        return null;
      }
    },

    // Test A's send, after the payment: the same send, answering why it didn't
    // go so the page can tell a closed casting call from a failure to retry.
    async sendAfterPayment(
      submissionId: number
    ): Promise<{ sent: ISendSubmissionResponse | null; error: string | null }> {
      dispatch.submissionStep4Model.setError(null);
      try {
        const resp = await getSubmissionFlowApi().sendSubmission(submissionId, {
          clientRequestId: sendRequestId(),
        });
        dispatch.submissionStep4Model.setSent(resp);
        return { sent: resp, error: null };
      } catch (err) {
        return { sent: null, error: errorOf(err).code };
      }
    },

    async holdSubmission(payload: {
      submissionId: number;
      request: IHoldSubmissionRequest;
    }) {
      dispatch.submissionStep4Model.setLoading(true);
      try {
        const hold = await getSubmissionFlowApi().holdSubmission(
          payload.submissionId,
          payload.request
        );
        dispatch.submissionStep4Model.setHold(hold);
        return hold;
      } catch (err) {
        dispatch.submissionStep4Model.setError(errorOf(err));
        return null;
      }
    },
  }),
});

// Restores the checkout across the full-page trip through Stripe, which takes
// React state with it.
export const readStoredCheckout = (): {
  checkoutSessionId: string;
  attemptId: number;
  returnPath: string;
  variant: Step4Variant;
} | null => {
  try {
    const raw = readStored(CHECKOUT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearStoredCheckout = () => {
  try {
    window.sessionStorage.removeItem(CHECKOUT_KEY);
  } catch (e) {
    // ignore
  }
};

export default submissionStep4Model;
